/**
 * Neko Sticky Buy Controls - Clone and Sync Pattern
 * Creates a sticky footer on mobile with quantity, inventory, and buy buttons
 * Syncs with variant changes by watching original elements for DOM mutations
 */

(function() {
  'use strict';

  // Only run on mobile
  function isMobile() {
    // Primary detection: Use matchMedia to match the CSS media query exactly
    const matchesMediaQuery = window.matchMedia('(max-width: 749px)').matches;

    // Fallback detection for Samsung Internet and other Android browsers
    // that may report viewport width incorrectly
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMobileUserAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|samsung/i.test(userAgent);
    const isSmallScreen = window.innerWidth <= 749 || window.screen.width <= 749;

    // Return true if EITHER matchMedia works OR it's a mobile device with small screen
    const result = matchesMediaQuery || (isMobileUserAgent && isSmallScreen);

    console.log('Sticky Buy Controls - Mobile Detection:', {
      matchesMediaQuery,
      isMobileUserAgent,
      isSmallScreen,
      innerWidth: window.innerWidth,
      screenWidth: window.screen.width,
      userAgent: userAgent.substring(0, 100),
      finalResult: result
    });

    return result;
  }

  function createStickyFooter() {
    if (!isMobile()) {
      console.log('Sticky Buy Controls: Not mobile, skipping');
      return;
    }

    // Check if sticky footer is enabled in theme settings
    const scriptTag = document.querySelector('script[src*="neko-sticky-buy-controls.js"]');
    const isEnabled = scriptTag?.getAttribute('data-sticky-footer-enabled');

    if (isEnabled === 'false') {
      console.log('Sticky Buy Controls: Disabled via theme settings');
      return;
    }

    console.log('Sticky Buy Controls: Starting initialization...');

    // Find original elements (DO NOT MOVE THEM - they must stay in place for Rise theme updates)
    const quantityBlock = document.querySelector('.product-form__quantity');
    const inventoryBlock = document.querySelector('.product__inventory');
    const buyButtonsParent = document.querySelector('product-form'); // <product-form> custom element

    console.log('Sticky Buy Controls: Found elements:', {
      quantityBlock: !!quantityBlock,
      inventoryBlock: !!inventoryBlock,
      buyButtonsParent: !!buyButtonsParent
    });

    if (!quantityBlock || !inventoryBlock || !buyButtonsParent) {
      console.error('Sticky Buy Controls: Required elements not found!');
      return;
    }

    // CLONE elements (deep clone with all children and attributes)
    // Originals stay in place for Rise theme to update
    let quantityClone = quantityBlock.cloneNode(true);
    let inventoryClone = inventoryBlock.cloneNode(true);
    let formClone = buyButtonsParent.cloneNode(true);

    console.log('Sticky Buy Controls: Elements cloned successfully');

    // Create sticky footer container
    const stickyFooter = document.createElement('div');
    stickyFooter.className = 'neko-pdp__sticky-footer';
    stickyFooter.appendChild(quantityClone);
    stickyFooter.appendChild(inventoryClone);
    stickyFooter.appendChild(formClone);

    // Append to body
    document.body.appendChild(stickyFooter);

    console.log('Sticky Buy Controls: Sticky footer created and appended to body');

    // Track if we're currently syncing to prevent infinite loops
    let isSyncing = false;

    // CRITICAL: Forward form submissions from clone to original
    function setupFormForwarding() {
      // Find submit buttons in both original and clone
      const originalSubmitBtn = buyButtonsParent.querySelector('button[type="submit"]');
      const clonedSubmitBtn = formClone.querySelector('button[type="submit"]');

      if (!originalSubmitBtn || !clonedSubmitBtn) {
        console.warn('Sticky Buy Controls: Submit buttons not found');
        return;
      }

      // Check if we already added the event listener
      if (clonedSubmitBtn.dataset.forwardingSetup === 'true') {
        return;
      }

      // Mark as set up
      clonedSubmitBtn.dataset.forwardingSetup = 'true';

      // Forward clicks from clone to original
      clonedSubmitBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        console.log('Sticky Buy Controls: Clone button clicked, forwarding to original');

        // Sync quantity from clone to original
        const cloneQty = formClone.querySelector('.quantity__input');
        const originalQty = buyButtonsParent.querySelector('.quantity__input');
        if (cloneQty && originalQty) {
          originalQty.value = cloneQty.value;
          console.log('Sticky Buy Controls: Synced quantity:', cloneQty.value);
        }

        // Trigger original button
        originalSubmitBtn.click();
      });

      // Handle Shopify payment buttons
      const clonedPaymentBtns = formClone.querySelectorAll('.shopify-payment-button__button');
      const originalPaymentBtns = buyButtonsParent.querySelectorAll('.shopify-payment-button__button');

      clonedPaymentBtns.forEach((clonedBtn, index) => {
        const originalBtn = originalPaymentBtns[index];
        if (originalBtn && clonedBtn.dataset.forwardingSetup !== 'true') {
          clonedBtn.dataset.forwardingSetup = 'true';

          clonedBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            console.log('Sticky Buy Controls: Payment button clicked, forwarding to original');

            // Sync quantity
            const cloneQty = formClone.querySelector('.quantity__input');
            const originalQty = buyButtonsParent.querySelector('.quantity__input');
            if (cloneQty && originalQty) {
              originalQty.value = cloneQty.value;
            }

            originalBtn.click();
          });
        }
      });

      console.log('Sticky Buy Controls: Form forwarding set up');
    }

    // Sync only INVENTORY and BUTTON TEXT/STATE - don't touch quantity or full form
    function syncInventoryAndButtons() {
      if (isSyncing) return;

      isSyncing = true;
      console.log('Sticky Buy Controls: Syncing inventory and buttons...');

      // 1. Sync inventory block (simple innerHTML copy is safe here)
      if (inventoryBlock && inventoryClone) {
        inventoryClone.innerHTML = inventoryBlock.innerHTML;
      }

      // 2. Sync button state and text (without replacing the whole form)
      const originalBtn = buyButtonsParent.querySelector('button[type="submit"]');
      const clonedBtn = formClone.querySelector('button[type="submit"]');

      if (originalBtn && clonedBtn) {
        // Copy button text
        const originalBtnText = originalBtn.querySelector('span');
        const clonedBtnText = clonedBtn.querySelector('span');
        if (originalBtnText && clonedBtnText) {
          clonedBtnText.textContent = originalBtnText.textContent;
        }

        // Copy disabled state
        if (originalBtn.hasAttribute('disabled')) {
          clonedBtn.setAttribute('disabled', 'disabled');
        } else {
          clonedBtn.removeAttribute('disabled');
        }

        // Copy aria-disabled
        const ariaDisabled = originalBtn.getAttribute('aria-disabled');
        if (ariaDisabled) {
          clonedBtn.setAttribute('aria-disabled', ariaDisabled);
        } else {
          clonedBtn.removeAttribute('aria-disabled');
        }
      }

      // 3. Sync quantity input attributes (min/max/step) without replacing the element
      const originalQtyInput = buyButtonsParent.querySelector('.quantity__input');
      const clonedQtyInput = formClone.querySelector('.quantity__input');

      if (originalQtyInput && clonedQtyInput) {
        // Copy attributes but preserve the value
        const currentValue = clonedQtyInput.value;

        ['min', 'max', 'step', 'data-min', 'data-max'].forEach(attr => {
          const value = originalQtyInput.getAttribute(attr);
          if (value !== null) {
            clonedQtyInput.setAttribute(attr, value);
          } else {
            clonedQtyInput.removeAttribute(attr);
          }
        });

        // Reset value to the new min if current value is below new min
        const newMin = parseInt(clonedQtyInput.getAttribute('min') || '1');
        if (parseInt(currentValue) < newMin) {
          clonedQtyInput.value = newMin;
        }
      }

      // 4. Sync hidden variant ID input
      const originalVariantInput = buyButtonsParent.querySelector('input[name="id"]');
      const clonedVariantInput = formClone.querySelector('input[name="id"]');

      if (originalVariantInput && clonedVariantInput) {
        clonedVariantInput.value = originalVariantInput.value;

        if (originalVariantInput.hasAttribute('disabled')) {
          clonedVariantInput.setAttribute('disabled', 'disabled');
        } else {
          clonedVariantInput.removeAttribute('disabled');
        }
      }

      console.log('Sticky Buy Controls: Sync complete');
      isSyncing = false;
    }

    // MutationObserver to watch originals for changes
    const observer = new MutationObserver((mutations) => {
      console.log('Sticky Buy Controls: DOM mutation detected:', mutations.length, 'mutations');

      // Debounce: wait a bit for all mutations to complete
      clearTimeout(observer.syncTimeout);
      observer.syncTimeout = setTimeout(() => {
        syncInventoryAndButtons();
      }, 100);
    });

    // Watch all three original elements
    const config = {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true
    };

    observer.observe(quantityBlock, config);
    observer.observe(inventoryBlock, config);
    observer.observe(buyButtonsParent, config);

    console.log('Sticky Buy Controls: MutationObserver set up for auto-sync');

    // Initial form forwarding setup
    setupFormForwarding();

    console.log('Sticky Buy Controls: Initial setup complete');
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', createStickyFooter);
  } else {
    createStickyFooter();
  }

  // Re-initialize on window resize if crossing mobile threshold
  let wasMobile = isMobile();
  window.addEventListener('resize', function() {
    const nowMobile = isMobile();
    if (wasMobile !== nowMobile) {
      wasMobile = nowMobile;
      window.location.reload();
    }
  });
})();
